import React, { useState, useMemo } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { QuickStatsCard } from '@shared/components/QuickStatsCard';
import {
  MapPin,
  User,
  ChevronDown,
  Route,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Hourglass,
  Eye,
} from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useRoutes, useMeterRunDetail, useMeterDispatchDetail } from '../../../hook';
import { PAGE_SIZE } from '@shared/utils/constants';

export interface RouteItem {
  id: string;
  route: string;
  area: string;
  assignedReader: string;
  utilityService: string;
  totalMeters: number;
  completed: number;
  pending: number;
  exceptions: number;
  scheduled: string;
  completedOn: string;
  status: string;
}

export interface RoutesTabProps {
  isMobileAssign?: boolean;
  onAssign?: (row: any) => void;
  onReassign?: (row: any) => void;
  onUnassign?: (row: any) => void;
}

export const RoutesTab: React.FC<RoutesTabProps> = ({
  isMobileAssign = false,
  onAssign,
  onReassign,
  onUnassign,
}) => {
  const { runId } = useParams();
  const [searchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  
  const isPhotoMeter = (searchParams.get('type') || '').toLowerCase() === 'mobile';
  
  const { data: meterRunDetail } = useMeterRunDetail(runId, remoteUtilityId);
  const cycleId = meterRunDetail?.meterSchedule?.meterCycle?.id;

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');


  const { data: routesData, isLoading: routesLoading } = useRoutes(
    page,
    !isMobileAssign ? remoteUtilityId : null,
    PAGE_SIZE,
    cycleId,
    searchQuery
  );

  const { data: dispatchData, isLoading: dispatchLoading } = useMeterDispatchDetail({
    meter_run_id: isMobileAssign ? runId : null,
    remote_utility_id: remoteUtilityId,
    page: page,
    origin: "Web",
    search: searchQuery
  });

  const isLoading = isMobileAssign ? dispatchLoading : routesLoading;
  const currentData = isMobileAssign ? dispatchData : routesData;

  const displayRoutes: RouteItem[] = useMemo(() => {
    const rawList = Array.isArray(currentData?.results) ? currentData.results : [];
    
    if (rawList && rawList.length > 0) {
      return rawList.map((item: any): RouteItem => {
        if (isMobileAssign) {
          return {
            id: String(item.id),
            route: item.routeName || 'N/A',
            area: Array.isArray(item.areaName) && item.areaName.length > 0 ? item.areaName.join(', ') : 'N/A',
            assignedReader: Array.isArray(item.meterReaderName) 
              ? item.meterReaderName.join(', ') 
              : item.meterReaderName || 'N/A',
            utilityService: Array.isArray(item.utilityService) && item.utilityService.length > 0 ? item.utilityService.join(', ') : 'N/A',
            totalMeters: item.meterCount || 0,
            completed: item.completedCount,
            pending: item.pendingCount,
            exceptions: item.exceptions || 0,
            scheduled: item.assignDate || item.createdDate || 'N/A',
            completedOn: item.completedDate || 'N/A',
            status:item.status || 'N/A',
          };
        }

        return {
          id: String(item.id),
          route: item.routeName || 'N/A',
          area: Array.isArray(item.areaName) && item.areaName.length > 0 ? item.areaName.join(', ') : 'N/A',
          assignedReader: item.assignedReader || item.createdUserRemoteName || 'N/A',
          utilityService: Array.isArray(item.utilityService) && item.utilityService.length > 0 ? item.utilityService.join(', ') : 'N/A',
          totalMeters: item.meterCount || 0,
          completed: item.completed || 0,
          pending: item.pending || 0,
          exceptions: item.exceptions || 0,
          scheduled: item.createdDate || 'N/A',
          completedOn: item.lastModifiedDate || 'N/A',
          status: item.status || (item.isActive ? 'Active' : 'N/A'),
        };
      });
    }
    return [];
  }, [currentData, isMobileAssign]);

  const columns: TableColumn<RouteItem>[] = [
    {
      key: 'route',
      header: 'Route',
      render: (val) => (
        <div className="flex items-center gap-1.5 text-xs">
          <MapPin className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          <span className="font-semibold text-gray-900">{val}</span>
        </div>
      ),
    },
    {
      key: 'area',
      header: 'Area',
      render: (val) => <span className="text-gray-600 text-xs">{val}</span>,
    },
    {
      key: 'assignedReader',
      header: 'Assigned Reader',
      render: (val) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-700">
          <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span>{val}</span>
        </div>
      ),
    },
    {
      key: 'utilityService',
      header: 'Utility Service',
      render: (val) => <span className="text-gray-600 text-xs">{val}</span>,
    },
    {
      key: 'totalMeters',
      header: 'Total Meters',
      render: (val) => <span className="font-semibold text-gray-900 text-xs">{val}</span>,
    },
    {
      key: 'completed',
      header: 'Completed',
      render: (val) => <span className="font-semibold text-emerald-600 text-xs">{val}</span>,
    },
    {
      key: 'pending',
      header: 'Pending',
      render: (val) => <span className="font-semibold text-amber-600 text-xs">{val}</span>,
    },
    {
      key: 'exceptions',
      header: 'Exceptions',
      render: (val) => (
        <span
          className={`font-semibold text-xs ${
            val > 0 ? 'text-red-600' : 'text-gray-500'
          }`}
        >
          {val}
        </span>
      ),
    },
    {
      key: 'scheduled',
      header: 'Scheduled',
      render: (val) => <span className="text-gray-600 text-xs">{val}</span>,
    },
    {
      key: 'completedOn',
      header: 'Completed On',
      render: (val) => <span className="text-gray-600 text-xs">{val}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (val) => {
        let badgeClass = 'bg-gray-100 text-gray-700 border-gray-200';
        if (val === 'Completed') {
          badgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
        } else if (val === 'Partial') {
          badgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
        } else if (val === 'In Progress') {
          badgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
        }
        return (
          <Badge
            variant="outline"
            className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${badgeClass}`}
          >
            {val}
          </Badge>
        );
      },
    },
    {
      key: 'id',
      header: 'Actions',
      render: () => (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-gray-100"
        >
          <Eye className="h-4 w-4 text-gray-400" />
        </Button>
      ),
    },
  ];

  const totalItems = currentData?.count || 0;

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems: totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalItems]
  );

  return (
    <div className="space-y-6 pt-2">
      {/* 1. QuickStats Cards Grid */}
      {isPhotoMeter && !isMobileAssign && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <QuickStatsCard
            title="Total Routes"
            count={routesData?.count || 0}
            icon={<Route className="w-5 h-5 text-gray-500" />}
          />
          <QuickStatsCard
            title="Completed"
            count={displayRoutes.filter(r => r.status === 'Completed').length}
            icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />}
          />
          <QuickStatsCard
            title="In Progress"
            count={displayRoutes.filter(r => r.status === 'In Progress').length}
            icon={<Clock className="w-5 h-5 text-blue-500" />}
          />
          <QuickStatsCard
            title="Partial"
            count={displayRoutes.filter(r => r.status === 'Partial').length}
            icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
          />
          <QuickStatsCard
            title="Pending"
            count={displayRoutes.filter(r => r.status === 'Pending').length}
            icon={<Hourglass className="w-5 h-5 text-yellow-600" />}
          />
        </div>
      )}

      {/* 2. GenericTable */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <GenericTable
          data={displayRoutes}
          columns={isMobileAssign ? columns.filter(col => col.key !== 'id') : columns}
          rowKey="id"
          loading={isLoading}
          searchPlaceholder="Search routes..."
          onSearch={(term) => {
            setSearchQuery(term);
            setPage(1);
          }}
          pagination={pagination}
          onPageChange={(p) => setPage(p)}
          emptyMessage="No routes found."
          actions={isMobileAssign ? [
            {
              label: "Assign",
              onClick: (row: any) => onAssign?.(row),
              hidden: (row: any) => row.status !== "Not Dispatched" && row.status !== "Unassigned",
            },
            {
              label: "Reassign",
              onClick: (row: any) => onReassign?.(row),
              hidden: (row: any) => row.status === "Not Dispatched" || row.status === "Unassigned",
            },
            {
              label: "Unassign",
              onClick: (row: any) => onUnassign?.(row),
              hidden: (row: any) => row.status === "Not Dispatched" || row.status === "Unassigned",
            },
          ] : undefined}
          actionsType={isMobileAssign ? "dropdown" : undefined}
        />
      </div>
    </div>
  );
};
export default RoutesTab;
